import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ArrowLeft,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Maximize2,
  MessageSquare,
  PanelRightClose,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  Button,
  SimpleTooltip,
  cn,
  useIsCompact,
  useShellPanel,
} from '@command-center/ui-system';
import { AlicePanelFrame, useAlicePanelShell } from './alice-panel-shell';
import { aliceAvatarUrl } from './assets';

export type RightSidebarPanelId = 'alice' | 'recordDetails' | 'messages';

export interface RightSidebarBookmark {
  id: string;
  label: string;
  icon?: LucideIcon;
  onOpen: () => void;
}

/**
 * A loaded record is deliberately a rendered workspace, not a route snapshot.
 * Keeping it in the provider above the router lets its tabs, forms, actions,
 * and query subscriptions remain available while the main page changes.
 */
export interface RightSidebarRecordDetails {
  key: string;
  recordId: string;
  recordTypeKey: string;
  title: string;
  content: ReactNode;
  /** Route prefix that originally loaded this record; closing elsewhere must not navigate back. */
  sourcePath?: string;
  onClear?: () => void;
  onOpenFullScreen?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  bookmarks?: RightSidebarBookmark[];
}

export interface RightSidebarRecordPresentation {
  icon?: LucideIcon;
  singularLabel?: string;
  canOpenFullScreen?: boolean;
}

export interface RightSidebarPanelControls {
  collapse: () => void;
}

type RightSidebarPanelContent =
  | ReactNode
  | ((controls: RightSidebarPanelControls) => ReactNode);

export interface RightSidebarContextValue {
  activePanel: RightSidebarPanelId;
  collapsed: boolean;
  openToken: number;
  recordDetails?: RightSidebarRecordDetails;
  loadRecordDetails: (record: RightSidebarRecordDetails) => void;
  openRecordDetails: () => void;
  clearRecordDetails: () => void;
  showPanel: (panel: RightSidebarPanelId) => void;
  collapse: () => void;
  expand: () => void;
}

interface InternalRightSidebarContextValue extends RightSidebarContextValue {
  width: number;
  startResize: ReturnType<typeof useAlicePanelShell>['startResize'];
  resizeFromKeyboard: ReturnType<typeof useAlicePanelShell>['resizeFromKeyboard'];
}

const RightSidebarContext = createContext<InternalRightSidebarContextValue | undefined>(undefined);

export function RightSidebarProvider({
  storageKey,
  defaultPanel = 'alice',
  children,
}: {
  storageKey: string;
  defaultPanel?: RightSidebarPanelId;
  children: ReactNode;
}) {
  const panel = useAlicePanelShell({ storageKey, defaultState: 'open' });
  const [activePanel, setActivePanel] = useState<RightSidebarPanelId>(() =>
    readPanel(storageKey, defaultPanel),
  );
  const [openToken, setOpenToken] = useState(0);
  const [recordDetails, setRecordDetails] = useState<RightSidebarRecordDetails>();

  const showPanel = useCallback(
    (next: RightSidebarPanelId) => {
      setActivePanel(next);
      writeStorage(storageKey, 'panel', next);
      panel.setState('open');
      setOpenToken((current) => current + 1);
    },
    [panel.setState, storageKey],
  );
  const loadRecordDetails = useCallback((record: RightSidebarRecordDetails) => {
    setRecordDetails(record);
  }, []);
  const openRecordDetails = useCallback(() => showPanel('recordDetails'), [showPanel]);
  const clearRecordDetails = useCallback(() => {
    setRecordDetails(undefined);
    setActivePanel((current) => {
      if (current !== 'recordDetails') return current;
      writeStorage(storageKey, 'panel', 'alice');
      return 'alice';
    });
  }, [storageKey]);
  const collapse = useCallback(() => panel.setState('collapsed'), [panel.setState]);
  const expand = useCallback(() => panel.setState('open'), [panel.setState]);

  const value = useMemo<InternalRightSidebarContextValue>(
    () => ({
      activePanel,
      collapsed: panel.state === 'collapsed',
      openToken,
      recordDetails,
      loadRecordDetails,
      openRecordDetails,
      clearRecordDetails,
      showPanel,
      collapse,
      expand,
      width: panel.width,
      startResize: panel.startResize,
      resizeFromKeyboard: panel.resizeFromKeyboard,
    }),
    [
      activePanel,
      clearRecordDetails,
      collapse,
      expand,
      loadRecordDetails,
      openRecordDetails,
      openToken,
      panel.resizeFromKeyboard,
      panel.startResize,
      panel.state,
      panel.width,
      recordDetails,
      showPanel,
    ],
  );

  return <RightSidebarContext.Provider value={value}>{children}</RightSidebarContext.Provider>;
}

export function useRightSidebar(): RightSidebarContextValue {
  const context = useContext(RightSidebarContext);
  if (!context) throw new Error('useRightSidebar must be used inside RightSidebarProvider.');
  return context;
}

/**
 * Loads or refreshes a record without clearing it when the calling route later
 * unmounts. A different key intentionally replaces the loaded record; updating
 * the same key refreshes its content and callbacks without stealing panel focus.
 */
export function useLoadedRecordDetails(
  record: RightSidebarRecordDetails | undefined,
  activate: boolean,
) {
  const { loadRecordDetails, openRecordDetails } = useRightSidebar();

  useEffect(() => {
    if (record) loadRecordDetails(record);
  }, [loadRecordDetails, record]);

  useEffect(() => {
    if (record && activate) openRecordDetails();
  }, [activate, openRecordDetails, record?.key]);
}

export function SharedRightSidebar({
  alice,
  aliceEnabled = true,
  aliceUnavailableLabel = 'Alice is unavailable in this workspace.',
  messages,
  messagesEnabled = false,
  messagesUnavailableLabel = 'Messages are not available in this workspace yet.',
  messagesBadge,
  recordPresentation,
  onPanelChange,
}: {
  alice: RightSidebarPanelContent;
  aliceEnabled?: boolean;
  aliceUnavailableLabel?: string;
  messages?: RightSidebarPanelContent;
  messagesEnabled?: boolean;
  messagesUnavailableLabel?: string;
  messagesBadge?: ReactNode;
  recordPresentation?: RightSidebarRecordPresentation;
  onPanelChange?: (panel: RightSidebarPanelId) => void;
}) {
  const drawer = useInternalRightSidebar();
  const shellPanel = useShellPanel();
  const RecordIcon = recordPresentation?.icon ?? ListChecks;

  useEffect(() => {
    if (drawer.activePanel === 'recordDetails' && !drawer.recordDetails) drawer.showPanel('alice');
    if (drawer.activePanel === 'messages' && !messagesEnabled) drawer.showPanel('alice');
  }, [drawer.activePanel, drawer.recordDetails, drawer.showPanel, messagesEnabled]);

  const showPanel = (panel: RightSidebarPanelId) => {
    if (panel === 'alice' && !aliceEnabled) return;
    if (panel === 'recordDetails' && !drawer.recordDetails) return;
    if (panel === 'messages' && !messagesEnabled) return;
    drawer.showPanel(panel);
    onPanelChange?.(panel);
  };
  const collapse = () => {
    if (shellPanel.inMobileOverlay) shellPanel.close();
    else drawer.collapse();
  };
  const clearRecord = () => {
    const sourcePath = drawer.recordDetails?.sourcePath;
    const sourceIsActive = !sourcePath
      || (typeof window !== 'undefined'
        && (window.location.pathname === sourcePath
          || window.location.pathname.startsWith(`${sourcePath}/`)));
    if (sourceIsActive) drawer.recordDetails?.onClear?.();
    drawer.clearRecordDetails();
    collapse();
  };
  const openRecordFullScreen = () => {
    drawer.recordDetails?.onOpenFullScreen?.();
    collapse();
  };
  const panelControls = { collapse };

  if (drawer.collapsed && !shellPanel.inMobileOverlay) {
    return (
      <RightSidebarRail
        activePanel={drawer.activePanel}
        aliceEnabled={aliceEnabled}
        messagesEnabled={messagesEnabled}
        messagesBadge={messagesBadge}
        record={drawer.recordDetails}
        recordIcon={RecordIcon}
        onExpand={drawer.expand}
        onShowPanel={showPanel}
      />
    );
  }

  return (
    <AlicePanelFrame
      width={drawer.width}
      onResizeStart={drawer.startResize}
      onResizeKeyDown={drawer.resizeFromKeyboard}
      resizeLabel="Resize right sidebar"
    >
      <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border px-2 pl-3">
        <PanelButton
          label={aliceEnabled ? 'Alice' : 'Alice unavailable'}
          icon={Sparkles}
          active={drawer.activePanel === 'alice'}
          disabled={!aliceEnabled}
          onClick={() => showPanel('alice')}
        />
        <PanelButton
          label={
            drawer.recordDetails
              ? `Record Details: ${drawer.recordDetails.title}`
              : 'Record Details'
          }
          icon={RecordIcon}
          active={drawer.activePanel === 'recordDetails'}
          disabled={!drawer.recordDetails}
          onClick={() => showPanel('recordDetails')}
        />
        <PanelButton
          label={messagesEnabled ? 'Messages' : 'Messages unavailable'}
          icon={MessageSquare}
          active={drawer.activePanel === 'messages'}
          disabled={!messagesEnabled}
          badge={messagesBadge}
          onClick={() => showPanel('messages')}
        />
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={collapse}
          aria-label={shellPanel.inMobileOverlay ? 'Close right sidebar' : 'Collapse right sidebar'}
        >
          {shellPanel.inMobileOverlay ? (
            <X className="h-4 w-4" aria-hidden />
          ) : (
            <PanelRightClose className="h-4 w-4" aria-hidden />
          )}
        </Button>
      </div>

      <SidebarPanel active={drawer.activePanel === 'alice'}>
        {aliceEnabled
          ? resolvePanelContent(alice, panelControls)
          : <UnavailablePanel icon={Sparkles} label={aliceUnavailableLabel} />}
      </SidebarPanel>

      <SidebarPanel active={drawer.activePanel === 'recordDetails'}>
        {drawer.recordDetails ? (
          <>
            <RecordDetailsHeader
              title={drawer.recordDetails.title}
              recordType={recordPresentation?.singularLabel}
              onClose={clearRecord}
              canGoPrevious={drawer.recordDetails.canGoPrevious}
              canGoNext={drawer.recordDetails.canGoNext}
              onPrevious={drawer.recordDetails.onPrevious}
              onNext={drawer.recordDetails.onNext}
              onOpenFullScreen={
                drawer.recordDetails.onOpenFullScreen && recordPresentation?.canOpenFullScreen !== false
                  ? openRecordFullScreen
                  : undefined
              }
            />
            <div key={drawer.recordDetails.key} className="min-h-0 flex-1 overflow-y-auto p-2.5">
              {drawer.recordDetails.content}
            </div>
          </>
        ) : (
          <UnavailablePanel icon={ListChecks} label="Select a record to open its details here." />
        )}
      </SidebarPanel>

      <SidebarPanel active={drawer.activePanel === 'messages'}>
        {messagesEnabled && messages ? (
          resolvePanelContent(messages, panelControls)
        ) : (
          <UnavailablePanel icon={MessageSquare} label={messagesUnavailableLabel} />
        )}
      </SidebarPanel>
    </AlicePanelFrame>
  );
}

function resolvePanelContent(
  content: RightSidebarPanelContent,
  controls: RightSidebarPanelControls,
) {
  return typeof content === 'function' ? content(controls) : content;
}

function useInternalRightSidebar(): InternalRightSidebarContextValue {
  const context = useContext(RightSidebarContext);
  if (!context) throw new Error('SharedRightSidebar must be used inside RightSidebarProvider.');
  return context;
}

function RecordDetailsHeader({
  title,
  recordType,
  canGoPrevious = false,
  canGoNext = false,
  onClose,
  onPrevious,
  onNext,
  onOpenFullScreen,
}: {
  title: string;
  recordType?: string;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onOpenFullScreen?: () => void;
}) {
  const compact = useIsCompact();
  const closeLabel = `Close ${recordType?.toLowerCase() ?? 'record'} details`;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-border/80 bg-muted/45 px-3 py-2 shadow-[inset_0_-1px_0_hsl(var(--background)/0.35)]">
      <Button
        type="button"
        size={compact ? 'sm' : 'icon'}
        variant="ghost"
        className={cn('h-8 flex-shrink-0 gap-1.5', compact ? 'px-2' : 'w-8')}
        onClick={onClose}
        aria-label={closeLabel}
        title={closeLabel}
      >
        {compact ? (
          <>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            <span className="text-[12px]">Back</span>
          </>
        ) : (
          <X className="h-4 w-4" aria-hidden />
        )}
      </Button>
      <div className="min-w-0 flex-1 basis-32">
        <div className="line-clamp-2 break-words text-[12px] leading-snug text-muted-foreground">
          {recordType ? `${recordType} > ${title}` : title}
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={onPrevious}
          disabled={!canGoPrevious}
          aria-label="Previous record"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={onNext}
          disabled={!canGoNext}
          aria-label="Next record"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
        {onOpenFullScreen && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={onOpenFullScreen}
            aria-label="Open record details full screen"
          >
            <Maximize2 className="h-4 w-4" aria-hidden />
          </Button>
        )}
      </div>
    </div>
  );
}

function RightSidebarRail({
  activePanel,
  aliceEnabled,
  messagesEnabled,
  messagesBadge,
  record,
  recordIcon: RecordIcon,
  onExpand,
  onShowPanel,
}: {
  activePanel: RightSidebarPanelId;
  aliceEnabled: boolean;
  messagesEnabled: boolean;
  messagesBadge?: ReactNode;
  record?: RightSidebarRecordDetails;
  recordIcon: LucideIcon;
  onExpand: () => void;
  onShowPanel: (panel: RightSidebarPanelId) => void;
}) {
  return (
    <div className="flex h-full w-[52px] flex-col items-center bg-card py-2">
      <SimpleTooltip text="Expand right sidebar" side="left">
        <button
          type="button"
          onClick={onExpand}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Expand right sidebar"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
      </SimpleTooltip>
      <div className="my-2 h-px w-7 bg-border" />
      <RailButton
        label={aliceEnabled ? 'Open Alice' : 'Alice unavailable'}
        active={activePanel === 'alice'}
        disabled={!aliceEnabled}
        onClick={() => onShowPanel('alice')}
      >
        <img src={aliceAvatarUrl} alt="" className="h-7 w-7 rounded-md object-cover" />
      </RailButton>
      <RailButton
        label={record ? `Open ${record.title}` : 'Record Details unavailable'}
        active={activePanel === 'recordDetails'}
        disabled={!record}
        onClick={() => onShowPanel('recordDetails')}
      >
        <RecordIcon className="h-4 w-4" aria-hidden />
      </RailButton>
      <RailButton
        label={messagesEnabled ? 'Messages' : 'Messages unavailable'}
        active={activePanel === 'messages'}
        disabled={!messagesEnabled}
        onClick={() => onShowPanel('messages')}
      >
        <span className="relative">
          <MessageSquare className="h-4 w-4" aria-hidden />
          {messagesBadge}
        </span>
      </RailButton>
      {(record?.bookmarks?.length ?? 0) > 0 && <div className="my-2 h-px w-7 bg-border" />}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {record?.bookmarks?.map((bookmark) => {
          const Icon = bookmark.icon ?? Bookmark;
          return (
            <RailButton key={bookmark.id} label={bookmark.label} onClick={bookmark.onOpen}>
              <Icon className="h-4 w-4" aria-hidden />
            </RailButton>
          );
        })}
      </div>
      <ChevronRight className="mb-1 h-3.5 w-3.5 text-muted-foreground/50" aria-hidden />
    </div>
  );
}

function PanelButton({
  label,
  icon: Icon,
  active,
  disabled,
  badge,
  onClick,
}: {
  label: string;
  icon: LucideIcon;
  active?: boolean;
  disabled?: boolean;
  badge?: ReactNode;
  onClick: () => void;
}) {
  return (
    <SimpleTooltip text={label} side="bottom">
      <span className="relative inline-flex">
        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-35',
            active && 'border-primary/20 bg-emphasis text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis',
          )}
          aria-label={label}
          aria-pressed={active}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </button>
        {badge}
      </span>
    </SimpleTooltip>
  );
}

function RailButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <SimpleTooltip text={label} side="left">
      <span className="inline-flex">
        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={cn(
            'my-0.5 flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-35',
            active && 'border-primary/25 bg-emphasis text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis',
          )}
          aria-label={label}
        >
          {children}
        </button>
      </span>
    </SimpleTooltip>
  );
}

function SidebarPanel({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <div
      className={cn('min-h-0 flex-1 flex-col overflow-hidden', active ? 'flex' : 'hidden')}
      aria-hidden={!active}
    >
      {children}
    </div>
  );
}

function UnavailablePanel({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center text-sm text-muted-foreground">
      <Icon className="h-6 w-6 text-muted-foreground/60" aria-hidden />
      <h2 className="font-semibold text-foreground">Unavailable</h2>
      <p>{label}</p>
    </div>
  );
}

function writeStorage(storageKey: string, part: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${storageKey}:${part}`, value);
  } catch {
    // Storage can be unavailable in privacy modes; in-memory state still works.
  }
}

function readPanel(storageKey: string, fallback: RightSidebarPanelId): RightSidebarPanelId {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = window.localStorage.getItem(`${storageKey}:panel`);
    return value === 'messages' || value === 'alice' ? value : fallback;
  } catch {
    return fallback;
  }
}
